/**
 * The lifecycle state names the dm TYPE PACKAGE ships (gh#2607 smoke bulgu 1).
 *
 * ── THE DEFECT THIS CLOSES, AND WHY IT WAS SILENT ───────────────────────────
 * `dmTypePackageSeed.ts` declares the states; `dmPresentation.ts` decides what colour
 * and shape each one wears. Those were two independent recordings of one vocabulary,
 * and the drift ran in the PERMISSIVE direction: a state the seed added and the web
 * map never heard about does not fail anything — it renders through the neutral
 * fallback, as a grey chip that says "no opinion" about a state the package has a
 * definite opinion on. MOST of the shipped states were in that condition when an
 * operator finally looked at the board; no count is written here, because a count in
 * prose is one more hand-kept copy of the thing this module exists to stop copying.
 *
 * A fallback cannot detect this, because the fallback is the correct answer for the
 * OTHER population of states — see below. Only a comparison against the shipped set
 * can, and a comparison needs a set both sides can name.
 *
 * ── WHAT IT IS NOT: IT IS NOT "EVERY STATE A dm ITEM CAN BE IN" ─────────────
 * `pm_type_def.lifecycle` is runtime DATA. A tenant may revise a document and add a
 * state this release has never heard of, and that state must still render. So this
 * tuple is the CODE-OWNED half only: the states the package itself seeds. Consumers
 * keep their fallback for the rest, and `statusRole`'s signature stays `(status:
 * string)` rather than narrowing to this union — narrowing it would turn a tenant's
 * legitimate extension into a type error at every call site.
 *
 * The distinction is what makes the pin worth having: "not in the map" now means
 * "a tenant added it", which is a true statement about an unrecognised state, instead
 * of "nobody got round to it", which is what it meant before.
 *
 * ── WHY `shared` AND NOT THE API PACKAGE ────────────────────────────────────
 * `packages/web` cannot import from `packages/api`, which is the whole reason the two
 * recordings could not be compared and the reason no parity test existed to write.
 * `PmAgentRunStatus` moved here for the identical reason (Faz 4 dilim-2) and the
 * agent-run block became a total `Record` the moment it did.
 *
 * ── ORDER CARRIES NO MEANING HERE ───────────────────────────────────────────
 * Unlike {@link DM_EFFORT_VALUES}, where smallest-first IS the contract, this is
 * declaration order — the packaged types, each type's states in the order its graph
 * lists them, first occurrence wins. Nothing may rank by index. A state's KIND is
 * decided by the consumer that needs one (the web map answers a presentation role);
 * putting a classification here would make this module a second authority over a
 * question no two consumers have to answer the same way. ONE exception, because two
 * packages DO answer it: {@link DM_PARKED_STATES} (the api seed and the web role map).
 *
 * Terminality is likewise NOT here, because it is a property of a state IN A GRAPH
 * rather than of the word: `blocked` absorbs nothing on `dm.item` and the type does not
 * carry it at all on `dm.development`. It stays on each graph's own state entry, where
 * two types are free to disagree.
 */
/**
 * The state names the package seeds. `as const` so the type below is a UNION of
 * literals — which is what lets a consumer declare a TOTAL `Record` over them and hear
 * about an addition from `tsc` rather than from a grey chip.
 *
 * Both directions are pinned in `dmTypePackage.test.ts`: every seeded state is a word
 * here, and every word here is seeded by at least one type. The second direction is
 * the one that keeps this list from outliving the seed — a state removed from every
 * graph and left standing here would buy a consumer a colour decision about a state
 * nothing can be in.
 */
export const DM_LIFECYCLE_STATE_VALUES = [
    'active',
    'paused',
    'done',
    'planned',
    'in_progress',
    'waiting_decision',
    'in_review',
    'blocked',
    'abandoned',
    'closing',
    'draft',
    'approved',
    'superseded',
    'proposed',
    'decided',
    'archived',
    'append',
    'open',
    'triaged',
    'resolved',
    'reopened',
    'closed',
    'implemented',
    'deprecated',
    // DM 2126 S1a — `dm.job`'s five new words (it shares `blocked` and `done` above).
    'queued',
    'claimed',
    'running',
    'failed',
    'cancelled',
    // DM W3-B P1 — `dm.policy`'s terminal (it shares `draft` and `active` above).
    'retired',
];
/**
 * PARKED states (DM W5-0b): stopped on purpose until a condition — not stuck (`blocked`)
 * and not finished. The match is by WORD and type-agnostic (dm.development's `paused` is
 * parked too); only dm.item GATES the condition (`park-reason`). Read by the api seed
 * (`DM_ITEM_PARKED_STATE`) and pinned against the web role map.
 */
export const DM_PARKED_STATES = ['paused'];
/**
 * W5-U1 — the words the tree's child counter files as CLOSED: every state some packaged type
 * declares `terminal`, plus the sprint's settled `abandoned`. Type-agnostic like
 * {@link DM_PARKED_STATES} (the counter reads a status histogram, not a graph). Pinned against
 * the seed both ways (`dmTypePackage.test.ts`) and against the web's count-group map.
 */
export const DM_CLOSED_COUNT_STATES = [
    'done',
    'abandoned',
    'superseded',
    'archived',
    'closed',
    'deprecated',
    'cancelled',
    'retired',
];
/**
 * ── NO `isDmLifecycleState` MEMBERSHIP GUARD HERE, AND THAT IS A DECISION ───
 * `dmEffort.ts` ships one and this module deliberately does not, because nothing
 * asks the question at runtime. Both consumers are STATIC: the seed's state names are
 * typed as {@link DmLifecycleState}, and the web's role map is a total `Record` over
 * it — neither narrows a value it received.
 *
 * The reason to leave it out rather than provide it "for later" is that the question
 * it answers is easy to answer WRONG: `false` means "a tenant authored this state",
 * which is legitimate, not invalid. A guard sitting here uncalled is one refactor away
 * from being read as a validator and used to refuse a tenant's own write — and the
 * repo's dead-code gate is what surfaced the absence of a caller in the first place.
 * Whoever needs it should add it together with that caller, so the two are reviewed
 * as one thing.
 */
//# sourceMappingURL=dmLifecycleState.js.map